import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum Theme {
    Light = 'light',
    Dark = 'dark',
    System = 'system',
}

const initTheme = () => {
    if (typeof window === 'undefined') {
        return Theme.System;
    }
    const theme = localStorage.getItem('theme') as Theme;

    return theme ?? Theme.System;
};

export const useTheme = () => {
    const { t } = useTranslation();
    const [theme, setTheme] = useState(initTheme);

    const themeOptions = [
        { label: t('Light Theme'), icon: 'light', value: Theme.Light },
        { label: t('Dark Theme'), icon: 'dark', value: Theme.Dark },
        { label: t('System Theme'), icon: 'systemTheme', value: Theme.System },
    ] as const;
    const currentTheme = themeOptions.find(({ value }) => value === theme) as (typeof themeOptions)[number];

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const isDark = theme === Theme.Dark || (theme === Theme.System && window.matchMedia('(prefers-color-scheme: dark)').matches);

        document.documentElement.classList.toggle('dark', isDark);
    }, [theme]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event: MediaQueryListEvent) => {
            if (theme === Theme.System) {
                document.documentElement.classList.toggle('dark', event.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const updateTheme = (value: Theme) => {
        setTheme(value);
        
        if (value === Theme.System) {
            localStorage.removeItem('theme');
        } else {
            localStorage.setItem('theme', value);
        }
    };

    return { theme, themeOptions, currentTheme, setTheme: updateTheme };
};
